import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Bus, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 10) {
      setError("Password must be at least 10 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-10">
      <div className="w-full max-w-md rounded-lg border border-border bg-white p-6 sm:p-8 shadow-sm">
        <div className="flex items-center gap-3 mb-6">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary">
            <Bus className="h-5 w-5 text-white" />
          </div>
          <h1 className="font-heading text-lg font-bold text-slate-900">Deluxe Travel Training</h1>
        </div>

        {done ? (
          <div data-testid="reset-password-success" className="text-center py-4">
            <CheckCircle2 className="mx-auto h-10 w-10 text-ukgreen mb-4" />
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-2">Password updated</h2>
            <p className="text-sm text-slate-600 mb-6">You can now sign in with your new password.</p>
            <Link to="/login" data-testid="reset-to-login-link" className="text-sm font-medium text-primary hover:underline">
              Go to sign in
            </Link>
          </div>
        ) : !token ? (
          <div data-testid="reset-password-invalid" role="alert">
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-2">Invalid reset link</h2>
            <p className="text-sm text-slate-600 mb-6">
              This password reset link is missing or invalid. Please request a new one.
            </p>
            <Link to="/forgot-password" data-testid="request-new-link" className="text-sm font-medium text-primary hover:underline">
              Request a new reset link
            </Link>
          </div>
        ) : (
          <>
            <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-2">Choose a new password</h2>
            <p className="text-sm text-slate-600 mb-6">Must be at least 10 characters.</p>
            {error && (
              <div data-testid="reset-password-error" role="alert" className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                {error}
              </div>
            )}
            <form onSubmit={onSubmit} className="space-y-4" data-testid="reset-password-form">
              <div className="space-y-1.5">
                <Label htmlFor="new-password">New password</Label>
                <Input
                  id="new-password"
                  data-testid="reset-new-password-input"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-11 focus:ring-2 focus:ring-blue-600"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="confirm-password">Confirm new password</Label>
                <Input
                  id="confirm-password"
                  data-testid="reset-confirm-password-input"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="h-11 focus:ring-2 focus:ring-blue-600"
                />
              </div>
              <Button
                data-testid="reset-password-submit-button"
                type="submit"
                disabled={loading}
                className="w-full h-11 bg-primary hover:bg-brand-hover text-white font-semibold"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
